import React, { useEffect, useState } from 'react';
import {
  allCategories,
  allStatuses,
  useGlobalDeckFilterStore,
} from '../stores/ui/globalDeckFilterStore';
import { TokenCategory, TokenStatus } from '../stores/data/types';

interface GlobalDeckFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalDeckFilterModal: React.FC<GlobalDeckFilterModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    selectedCategories,
    selectedStatuses,
    setCategories,
    setStatuses,
    reset,
  } = useGlobalDeckFilterStore();

  const [categories, setLocalCategories] = useState<TokenCategory[]>([]);
  const [statuses, setLocalStatuses] = useState<TokenStatus[]>([]);

  useEffect(() => {
    if (isOpen) {
      setLocalCategories(selectedCategories);
      setLocalStatuses(selectedStatuses);
    }
  }, [isOpen, selectedCategories, selectedStatuses]);

  const toggleCategory = (category: TokenCategory) => {
    setLocalCategories((prev) =>
      prev.includes(category)
        ? prev.filter((item) => item !== category)
        : [...prev, category],
    );
  };

  const toggleAllCategories = () => {
    const allSelected = categories.length === allCategories.length;
    setLocalCategories(allSelected ? [] : [...allCategories]);
  };

  const toggleStatus = (status: TokenStatus) => {
    setLocalStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status],
    );
  };

  const toggleAllStatuses = () => {
    const allSelected = statuses.length === allStatuses.length;
    setLocalStatuses(allSelected ? [] : [...allStatuses]);
  };

  const handleApply = () => {
    setCategories(categories);
    setStatuses(statuses);
    onClose();
  };

  const handleReset = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-gray-800 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            Global Deck Filters
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-white"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm font-medium text-gray-200">
              <span>Category</span>
              <button
                onClick={toggleAllCategories}
                className="rounded bg-gray-700 px-2 py-1 text-xs text-gray-200 transition-colors hover:bg-gray-600"
              >
                {categories.length === allCategories.length ? '모두 해제' : '모두 선택'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {allCategories.map((category) => (
                <label
                  key={category || 'none'}
                  className="flex cursor-pointer items-center space-x-2 rounded bg-gray-700 px-3 py-2 text-sm text-gray-100 hover:bg-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={categories.includes(category)}
                    onChange={() => toggleCategory(category)}
                    className="h-4 w-4 accent-amber-500"
                  />
                  <span>{category === TokenCategory.NONE ? '(Empty)' : category}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between text-sm font-medium text-gray-200">
              <span>Status</span>
              <button
                onClick={toggleAllStatuses}
                className="rounded bg-gray-700 px-2 py-1 text-xs text-gray-200 transition-colors hover:bg-gray-600"
              >
                {statuses.length === allStatuses.length ? '모두 해제' : '모두 선택'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {allStatuses.map((status) => (
                <label
                  key={status}
                  className="flex cursor-pointer items-center space-x-2 rounded bg-gray-700 px-3 py-2 text-sm text-gray-100 hover:bg-gray-600"
                >
                  <input
                    type="checkbox"
                    checked={statuses.includes(status)}
                    onChange={() => toggleStatus(status)}
                    className="h-4 w-4 accent-amber-500"
                  />
                  <span>{status === 'None' ? '(Empty)' : status}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={handleReset}
            className="rounded bg-gray-600 px-4 py-2 text-sm text-white transition-colors hover:bg-gray-500"
          >
            초기화
          </button>
          <button
            onClick={handleApply}
            className="rounded bg-amber-600 px-5 py-2 text-sm text-white transition-colors hover:bg-amber-500"
          >
            설정
          </button>
        </div>
      </div>
    </div>
  );
};

export default GlobalDeckFilterModal;

